import { Injectable } from '@angular/core';
import { CsvModes } from '../core/csv-modes.class';
import * as XLSX from 'xlsx';

@Injectable({
  providedIn: 'root'
})
export class ExportCsvService {

  getExportFilename(mode: string, asOfDateString: string, portfolioName: string, seriesName: string, holdingName?: string) {
    const asOfDate = new Date(asOfDateString + 'T00:00:00')
    const asofdate = (asOfDate.getMonth() + 1) + '-' + asOfDate.getDate() + '-' + asOfDate.getFullYear();
    const currentDateobj = new Date();
    const currentDate = (currentDateobj.getMonth() + 1) + '-' + currentDateobj.getDate() + '-' + currentDateobj.getFullYear();
    let fileName: string;
    if (mode === CsvModes.assets) {
      fileName = mode + "_" + portfolioName + "_" + seriesName + "_" + holdingName + "_as of_" + asofdate + "_" + currentDate;
    }
    else {
      fileName = mode + "_" + portfolioName + "_" + seriesName + "_as of_" + asofdate + "_" + currentDate;
    }
    return fileName;
  }

  getCsvHeader(mode: string, dealClassificationLabel = 'Strategy') {
    if (mode === CsvModes.quarterlyRollForwardDeals) {
      return {
        'invType': 'Investment Type', 'dealName': 'Deal Name', 'invStatus': 'Investment Status',
        'dealPriorNav': 'Prior Quarter Net Asset Value', 'dealQoqContributions': 'QoQ Contribution',
        'dealQoqDistributions': 'QoQ Distributions', 'dealGainLoss': 'Gain/Loss', 'dealCurrentNav': 'Current Quarter Net Asset Value', 'dealQoqTvpiChange': 'QoQ Change TVPI', 'dealQoqIrrChange': 'QoQ Change IRR',
      }
    }
    else if (mode === CsvModes.quarterlyRollForwardHoldings) {
      return {
        'invType': 'Investment Type', 'dealName': 'Deal Name', 'holdingName': 'Holding Name', 'invStatus': 'Investment Status',
        'holdingPriorNav': 'Prior Quarter Net Asset Value', 'holdingQoqContributions': 'QoQ Contribution',
        'holdingQoqDistributions': 'QoQ Distributions', 'holdingGainLoss': 'Gain/Loss', 'holdingCurrentNav': 'Current Quarter Net Asset Value', 'holdingQoqTvpiChange': 'QoQ Change TVPI', 'holdingQoqIrrChange': 'QoQ Change IRR',
      }
    }
    else if (mode === CsvModes.qRollForwardInvestmentType) {
      return {
        'invType': 'Investment Type', 'invTypePriorNav': 'Investment Type Prior Quarter Net Asset Value ',
        'invTypeQoqContributions': 'Investment Type QoQ Contribution', 'invTypeQoqDistributions': 'Investment Type QoQ Distributions', 'invTypeGainLoss': 'Investment Type Gain/Loss', 'invTypeCurrentNav': 'Investment Type Current Quarter Net Asset Value',
        'invTypeQoqTvpiChange': 'Investment Type QoQ Change TVPI', 'invTypeQoqIrrChange': 'Investment Type QoQ Change IRR'
      }
    }
    else if (mode === CsvModes.InvestmentType) {
      return {
        'invType': 'Investment Type', 'invTypeCommitment': 'Commitment', 'invTypeReportedContributions': 'Reported Contributions', 'invTypeContributions': 'Contributions', 'invTypeRemainingCommitment': 'Remaining Commitment',
        'invTypeReportedDistributions': 'Reported Distributions', 'invTypeDistributions': 'Distributions', 'invTypeReportedValue': 'Reported Value', 'invTypeSubsequentContributions': 'Subsequent Contributions',
        'invTypeSubsequentDistributions': 'Subsequent Distributions', 'invTypeFxGainLoss': 'Subsequent Investment Gain/(Loss)', 'invTypeNav': 'Net Asset Value', 'invTypeTotalValue': 'Total Value', 'invTypeTvpi': 'TVPI', 'invTypeIrr': 'IRR'
      }
    }
    else if (mode === CsvModes.seriesHoldings) {
      return {
        'invType': 'Investment Type', 'dealName': 'Deal Name', 'holdingName': 'Holding Name', 'invStatus': 'Investment Status', 'strategy': dealClassificationLabel, 'vintageYear': 'Vintage Year', 'valuationDateFormatted': 'Valuation Date',
        'holdingCommitment': 'Commitment', 'holdingReportedContributions': 'Reported Contributions', 'holdingContributions': 'Contributions', 'holdingRemainingCommitment': 'Remaining Commitment', 'holdingReportedDistributions': 'Reported Distributions',
        'holdingDistributions': 'Distributions', 'holdingReportedValue': 'Reported Value', 'holdingSubsequentContributions': 'Subsequent Contributions', 'holdingSubsequentDistributions': 'Subsequent Distributions', 'holdingFxGainLoss': 'Subsequent Investment Gain/(Loss)',
        'holdingNav': 'Net Asset Value', 'holdingTotalValue': 'Total Value',
      }
    }
    else if (mode === CsvModes.seriesDeals) {
      return {
        'invType': 'Investment Type', 'dealName': 'Deal Name', 'invStatus': 'Investment Status', 'strategy': dealClassificationLabel, 'vintageYear': 'Vintage Year', 'valuationDateFormatted': 'Valuation Date',
        'dealCommitment': 'Commitment', 'dealReportedContributions': 'Reported Contributions', 'dealContributions': 'Contributions', 'dealRemainingCommitment': 'Remaining Commitment', 'dealReportedDistributions': 'Reported Distributions',
        'dealDistributions': 'Distributions', 'dealReportedValue': 'Reported Value', 'dealSubsequentContributions': 'Subsequent Contributions', 'dealSubsequentDistributions': 'Subsequent Distributions', 'dealFxGainLoss': 'Subsequent Investment Gain/(Loss)',
        'dealNav': 'Net Asset Value', 'dealTotalValue': 'Total Value', 'dealTVPI': 'TVPI', 'dealIRR': 'IRR'
      }
    }
    else {
      return {
        'assetName': 'Asset Name', 'currency': 'Currency', 'acquisitionDate': 'Acquisition Date', 'remainingCost': 'Remaining Cost Basis', 'remainingMarketValue': 'Remaining Market Value', 'statementDate': 'Statement Date', 'sector': 'Sector',
        'subSector': 'Sub-Sector', 'geography': 'Geography', 'country': 'Country', 'status': 'Status', 'exitDate': 'Exit Date'
      }
    }
  }

  sortArrayAscending<T extends { name: string }>(arr: T[]): T[] {
    return arr.sort((a, b) => a.name.localeCompare(b.name));
  }

  exportToExcelFile(filename: string, worksheets: { name: string; csvArray: Record<string, unknown>[] }[]) {
    const sheets: Record<string, XLSX.WorkSheet> = {};
    const sheetNames: string[] = [];
    worksheets.forEach((worksheet) => {
      sheets[worksheet.name] = XLSX.utils.json_to_sheet(worksheet.csvArray, { skipHeader: true });
      sheetNames.push(worksheet.name)
    })
    const workBook: XLSX.WorkBook = {
      Sheets: sheets,
      SheetNames: sheetNames
    }
    XLSX.writeFile(workBook, filename + '.xlsx', { bookType: 'xlsx', type: 'array' });
  }
}
